/**
 * Dónde se pega el código que llegó por correo (GKTIBO-79).
 *
 * Esta pantalla no existía. El registro decía «revisá tu correo» y el código no
 * tenía dónde ir: el alta terminaba ahí, y ningún dueño llegaba a su primer
 * paso. Lo encontró el PO probando, no ningún test.
 *
 * Confirmar hace **dos** cosas, y la segunda es la que faltaba: valida el código
 * contra Cognito y **deja escrito el registro del usuario con su negocio**. Sin
 * eso, confirmar no alcanza — el backend sigue devolviendo 401.
 */

import { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { api } from '../../lib/api.ts';
import { textos } from './textos';

interface PasoDeCodigoProps {
  correo: string;
  /** Con el `tenantId` ya en la mano: es a dónde entra el dueño. */
  onConfirmado: (tenantId: string) => void;
}

export function PasoDeCodigo({ correo, onConfirmado }: PasoDeCodigoProps) {
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      const { tenantId } = await api.alta.confirmar(correo, codigo.trim());
      onConfirmado(tenantId);
    } catch (err) {
      // El backend distingue «equivocado» de «vencido» y manda el texto ya
      // escrito para el dueño. Reescribirlo acá los colapsaría en uno solo.
      const mensaje =
        typeof err === 'object' && err !== null ? String(Reflect.get(err, 'message') ?? '') : '';
      setError(mensaje || textos.registro.noSePudo);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="alta-codigo" onSubmit={(e) => void confirmar(e)} data-testid="alta-codigo">
      <p data-testid="registro-listo">{textos.registro.listoTexto}</p>

      <Input
        label={textos.registro.codigoEtiqueta}
        name="codigo"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
      />

      {error && (
        <p className="alta-codigo-error" role="alert" data-testid="alta-codigo-error">
          {error}
        </p>
      )}

      <Button type="submit" disabled={codigo.trim().length === 0 || enviando}>
        {enviando ? textos.registro.codigoConfirmando : textos.registro.codigoConfirmar}
      </Button>

      {/* Antes de que lo pregunte. */}
      <p className="alta-codigo-ayuda">{textos.registro.codigoNoLlego}</p>
    </form>
  );
}
